/**
 * Result of pitch detection analysis
 */
export interface PitchDetectionResult {
  /**
   * Detected frequency in Hz, or null if no pitch detected
   */
  frequency: number | null;

  /**
   * Confidence score between 0 and 1
   * Higher values indicate more reliable detection
   */
  confidence: number;

  /**
   * Optional note name (e.g., "C4", "A#5", "Bb4")
   * Only provided if frequency is detected and can be mapped to a note
   */
  note?: string;

  /**
   * Optional cents deviation from the nearest note
   * Positive = sharp, negative = flat
   */
  centsOff?: number;
}

/**
 * Configuration options for pitch detector
 */
export interface PitchDetectorConfig {
  /**
   * Minimum frequency to detect in Hz
   * @default 80
   */
  minFrequency?: number;

  /**
   * Maximum frequency to detect in Hz
   * @default 4835
   */
  maxFrequency?: number;

  /**
   * Confidence threshold below which detections are rejected
   * @default 0.8
   */
  confidenceThreshold?: number;

  /**
   * Sample rate of audio data
   * Usually determined by AudioContext
   */
  sampleRate?: number;
}

/**
 * Abstract interface for pitch detection implementations
 * Allows swapping underlying libraries without changing consuming code
 */
export interface PitchDetector {
  /**
   * Detect pitch from audio data
   * @param audioData Float32Array of audio samples
   * @param sampleRate Sample rate in Hz (e.g., 44100)
   * @returns Pitch detection result
   */
  detectPitch(
    audioData: Float32Array,
    sampleRate: number
  ): PitchDetectionResult;

  /**
   * Configure the frequency range to analyze
   * @param min Minimum frequency in Hz
   * @param max Maximum frequency in Hz
   */
  setFrequencyRange(min: number, max: number): void;

  /**
   * Set confidence threshold for pitch detection
   * @param threshold Value between 0 and 1
   */
  setConfidenceThreshold(threshold: number): void;

  /**
   * Get the name/type of this detector implementation
   */
  getName(): string;
}

/**
 * Preset configurations for different instruments
 */
export const INSTRUMENT_PRESETS = {
  flute: {
    minFrequency: 250, // C4 is ~262 Hz
    maxFrequency: 2200, // C7 is ~2093 Hz
    confidenceThreshold: 0.85,
  },
  voice: {
    minFrequency: 80, // E2
    maxFrequency: 1000, // C6
    confidenceThreshold: 0.8,
  },
  guitar: {
    minFrequency: 80, // E2 (low E string)
    maxFrequency: 1500, // G6
    confidenceThreshold: 0.85,
  },
} as const;

export type InstrumentPreset = keyof typeof INSTRUMENT_PRESETS;

/**
 * Raw note representation using MIDI as absolute pitch
 * This is the core type for representing musical notes throughout the system
 */
export interface RawNote {
  /**
   * MIDI note number (absolute pitch)
   * @example 60 = C4, 61 = C#4/Db4, 72 = C5
   */
  midi: number;

  /**
   * Optional spelling hint for enharmonic notes
   * Determines whether to render as sharp or flat notation
   * If omitted, spelling is chosen based on key signature context
   * @example midi: 61 with spelling: "sharp" → C#, with spelling: "flat" → Db
   */
  spelling?: "sharp" | "flat";
}

/**
 * Convert a note name to MIDI number
 * @param noteName Note name (e.g., "C4", "A#5", "Bb4")
 * @returns MIDI number, or null if invalid
 */
export function noteNameToMidi(noteName: string): number | null {
  const noteMatch = noteName.match(/^([A-G])([#b]?)(\d+)$/i);
  if (!noteMatch) return null;

  const [, letter, accidental, octaveStr] = noteMatch;
  const octave = parseInt(octaveStr, 10);

  // Map note letters to semitones (C = 0)
  const noteToSemitone: Record<string, number> = {
    C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11,
  };

  let semitone = noteToSemitone[letter.toUpperCase()];
  if (semitone === undefined) return null;

  // Apply accidental
  if (accidental === '#') semitone += 1;
  else if (accidental === 'b') semitone -= 1;

  // MIDI formula: (octave + 1) * 12 + semitone
  return (octave + 1) * 12 + semitone;
}

/**
 * Convert PitchDetectionResult to MIDI number
 * @param result Pitch detection result
 * @returns MIDI number if note detected, null otherwise
 */
export function pitchResultToMidi(result: PitchDetectionResult): number | null {
  if (!result.note || result.frequency === null) {
    return null;
  }
  return noteNameToMidi(result.note);
}

/**
 * Note letter with possible accidental
 */
export type NoteWithAccidental =
    | 'C' | 'C#' | 'Db'
    | 'D' | 'D#' | 'Eb'
    | 'E'
    | 'F' | 'F#' | 'Gb'
    | 'G' | 'G#' | 'Ab'
    | 'A' | 'A#' | 'Bb'
    | 'B';

/**
 * Full note name with octave (e.g., "C4", "F#5", "Bb3")
 */
export type NoteName = `${NoteWithAccidental}${number}`;
